import { FunctionComponent, SVGProps } from 'react'

import { ReactComponent as GotoIcon } from '../assets/images/integration-logos/color/goto-logo.svg'
import { ReactComponent as TeamsIcon } from '../assets/images/integration-logos/color/microsoft-teams-logo.svg'
import { ReactComponent as ZoomIcon } from '../assets/images/integration-logos/color/zoom-logo.svg'
import { ReactComponent as IntulseIcon } from '../assets/images/intulse-logo.svg'
import { ReactComponent as RingCentralIcon } from '../assets/images/ringcentral-logo.svg'
import { generateId } from './util'

import { SelectOption } from '../components/SelectDropdown/SelectDropdown'
import { type ExportOptionsPayload, type IntegrationEntityType } from '../models/Integration'
import { AccountIntegrationConfigMappingType, type CRMAccountIntegrationConfig } from '../models/Account'
import { type ExternalUserServiceIntegration } from '../models/User'
import { SessionStorageData } from '../models/SessionStorage'
import { BUSINESS_EVENT_DETAILS_MAPPING, BUSINESS_EVENT_LABEL_MAP, BusinessEventType } from '../models/BusinessEvent'
import { MentionData } from '../apps/professional-app/fa/MeetingSummary/MeetingResults/Lexical/Nodes/MentionNode/MentionNode'

export const PHONE_RECORDING_CAPABILITY = 'phone_recording:manage'

export const createState = (
  redirectUri: string | null = null,
  step: string | null = null,
  customDomain: string | null = null,
  environment: string | null = null,
): string => {
  const nonce = generateId()

  const data = JSON.stringify({
    redirectUri: redirectUri || window.location.origin + window.location.pathname,
    createdAt: new Date().getTime(),
    ...(step && { step }),
    ...(customDomain && { customDomain }),
    ...(environment && { environment }),
  })

  window.sessionStorage.setItem(nonce, data)
  return nonce
}

const getStorageKeyAndDomain = (
  nonce: string,
  integrationServiceId: IntegrationServiceId | null,
): {
  storageKey: string
  customDomain?: string
} => {
  const parts = usesCustomDomainSSO(integrationServiceId) ? nonce.split('|') : []

  return {
    storageKey: parts.length === 2 ? parts[0] : nonce,
    customDomain: parts.length === 2 ? parts[1] : undefined,
  }
}

export const readState = (nonce: string, integrationServiceId: IntegrationServiceId | null = null): SessionStorageData | null => {
  const { storageKey, customDomain } = getStorageKeyAndDomain(nonce, integrationServiceId)

  const data = window.sessionStorage.getItem(storageKey)
  if (!data) {
    return null
  }

  const parsedData = JSON.parse(data) as SessionStorageData

  return {
    ...parsedData,
    customDomain: customDomain ?? parsedData.customDomain ?? null,
  }
}

export type CallSolution = {
  id: string
  displayName: string
  icon: FunctionComponent<SVGProps<SVGSVGElement> & { title?: string | undefined }>
  getLink: (phoneNumber: string, code: string) => string
}
export const supportedCallSolutions: CallSolution[] = [
  {
    id: 'ringcentral',
    displayName: 'RingCentral',
    icon: RingCentralIcon,
    getLink: (phoneNumber: string, code: string) => `rcapp://r/call?number=${phoneNumber},,${code}`,
  },
  {
    id: 'intulse',
    displayName: 'Intulse',
    icon: IntulseIcon,
    getLink: (phoneNumber: string, code: string) => `tel:${phoneNumber},,${code}`,
  },
  {
    id: 'teams',
    displayName: 'Teams Phone',
    icon: TeamsIcon,
    getLink: (phoneNumber: string, code: string) => `https://teams.microsoft.com/l/call/0/0?users=4:${phoneNumber}#${code}`,
  },
  {
    id: 'zoom',
    displayName: 'Zoom Phone',
    icon: ZoomIcon,
    getLink: (phoneNumber: string, code: string) => `zoomphonecall://${phoneNumber},,${code}`,
  },
  {
    id: 'goto',
    displayName: 'GoTo Connect',
    icon: GotoIcon,
    getLink: (phoneNumber: string, code: string) => `tel:${phoneNumber},,${code}`,
  },
]

export const getMailIntegrationServiceLabel = (serviceId: string) => {
  if (serviceId === IntegrationServiceId.OUTLOOK_MAIL) {
    return 'Outlook'
  }
  if (serviceId === IntegrationServiceId.EXCHANGE_MAIL) {
    return 'Exchange'
  }
  return serviceId.charAt(0).toUpperCase() + serviceId.slice(1)
}

export enum IntegrationServiceId {
  // Meeting
  PHONE = 'phone',
  ZOOM = 'zoom',
  TEAMS = 'teams',
  MEET = 'meet',
  WEBEX = 'webex',
  RINGCENTRAL = 'ringcentral',
  GOTO = 'goto',
  TEAMS_CALLS = 'teamsCalls',
  // Calendar
  GOOGLE_CALENDAR = 'googleCalendar',
  OUTLOOK_CALENDAR = 'outlookCalendar',
  EXCHANGE_CALENDAR = 'exchangeCalendar',
  WEALTHBOX_CALENDAR = 'wealthboxCalendar',
  // Email
  GMAIL = 'gmail',
  OUTLOOK_MAIL = 'outlookMail',
  EXCHANGE_MAIL = 'exchangeMail',
  // Marketing & Social
  LINKEDIN = 'linkedin',
  // Financial Planning
  CONQUEST_PLANNING = 'conquestplanning',
  E_MONEY = 'emoney',
  RIGHT_CAPITAL = 'rightcapital',
  // Tax Planning
  HOLISTIPLAN = 'holistiplan',
  // Estate Planning
  WEALTH = 'wealth',
  // Portfolio Management & Reporting
  ORION = 'orion',
  BLACK_DIAMOND_WP = 'blackdiamondwp',
  // CRM
  ADVISOR_ENGINE = 'advisorengine',
  DYNAMICS = 'dynamics',
  HUBSPOT = 'hubspot',
  INVESTCLOUD = 'investcloud',
  PRACTIFI = 'practifi',
  REDTAIL = 'redtail',
  SALESFORCE = 'salesforce',
  SMARTOFFICE = 'smartoffice',
  BLACK_DIAMOND = 'blackdiamond', // SS&C BlackDiamond Edge
  ELEMENTS = 'elements', // SS&C BlackDiamond Elements
  ENGAGE = 'engage', // SS&C BlackDiamond Engage
  WEALTHBOX = 'wealthbox',
  XLR8 = 'xlr8',
  ZOHO = 'zoho',
  // Forms & Client Onboarding
  PRECISE_FP = 'precisefp',
  // Custodian
  SCHWAB = 'schwab',
  // Document
  BOX = 'box',
  EGNYTE = 'egnyte',
  GOOGLE_DRIVE = 'googledrive',
  ONEDRIVE = 'onedrive',
  SHAREPOINT = 'sharepoint',
  // Other
  CLIC = 'clic',
  CUSTOM = 'custom',
  WOVE = 'wove',
  DELEGATED_EMAIL = 'delegatedEmail',
}

const INTEGRATION_SERVICE_IDS_REQUIRING_UPDATE_ID_FIELD: IntegrationServiceId[] = [IntegrationServiceId.DYNAMICS, IntegrationServiceId.ENGAGE]

export const serviceIdRequiresIdFieldForUpdates = (serviceId: IntegrationServiceId): boolean =>
  INTEGRATION_SERVICE_IDS_REQUIRING_UPDATE_ID_FIELD.includes(serviceId)

const CUSTOM_DOMAIN_SSO_INTEGRATIONS: IntegrationServiceId[] = [IntegrationServiceId.DYNAMICS, IntegrationServiceId.ENGAGE, IntegrationServiceId.SHAREPOINT]

export const usesCustomDomainSSO = (serviceId: IntegrationServiceId | null): boolean => serviceId !== null && CUSTOM_DOMAIN_SSO_INTEGRATIONS.includes(serviceId)

const REFERRER_CUSTOM_DOMAIN_INTEGRATIONS: IntegrationServiceId[] = [IntegrationServiceId.EGNYTE]

export const usesReferrerCustomDomain = (serviceId: IntegrationServiceId | null): boolean =>
  serviceId !== null && REFERRER_CUSTOM_DOMAIN_INTEGRATIONS.includes(serviceId)

export enum IntegrationCategory {
  MEETING = 'meeting',
  CALLS = 'calls',
  CALENDAR = 'calendar',
  MAIL = 'mail',
  MARKETING_SOCIAL = 'marketing_social',
  PLANNING = 'planning',
  TAX = 'tax',
  ESTATE_PLANNING = 'estate_planning',
  PORTFOLIO = 'portfolio',
  CRM = 'crm',
  ONBOARDING = 'onboarding',
  DOCUMENT = 'document',
  CLIC = 'clic',
  CUSTODIAN = 'custodian',
}

const INTEGRATION_CATEGORY_MAP: Record<IntegrationCategory, IntegrationServiceId[]> = {
  [IntegrationCategory.CALENDAR]: [IntegrationServiceId.GOOGLE_CALENDAR, IntegrationServiceId.OUTLOOK_CALENDAR, IntegrationServiceId.EXCHANGE_CALENDAR],
  // DELEGATED_EMAIL is a CALENDAR integration in iam
  [IntegrationCategory.MAIL]: [
    IntegrationServiceId.GMAIL,
    IntegrationServiceId.OUTLOOK_MAIL,
    IntegrationServiceId.EXCHANGE_MAIL,
    IntegrationServiceId.DELEGATED_EMAIL,
  ],
  [IntegrationCategory.MARKETING_SOCIAL]: [IntegrationServiceId.LINKEDIN],
  [IntegrationCategory.PLANNING]: [IntegrationServiceId.CONQUEST_PLANNING, IntegrationServiceId.E_MONEY, IntegrationServiceId.RIGHT_CAPITAL],
  [IntegrationCategory.PORTFOLIO]: [IntegrationServiceId.ORION, IntegrationServiceId.WOVE, IntegrationServiceId.BLACK_DIAMOND_WP],
  [IntegrationCategory.TAX]: [IntegrationServiceId.HOLISTIPLAN],
  [IntegrationCategory.ONBOARDING]: [IntegrationServiceId.PRECISE_FP],
  [IntegrationCategory.MEETING]: [IntegrationServiceId.MEET, IntegrationServiceId.TEAMS, IntegrationServiceId.ZOOM, IntegrationServiceId.WEBEX],
  [IntegrationCategory.CALLS]: [IntegrationServiceId.PHONE, IntegrationServiceId.GOTO, IntegrationServiceId.RINGCENTRAL, IntegrationServiceId.TEAMS_CALLS],
  [IntegrationCategory.CRM]: [
    IntegrationServiceId.WEALTHBOX,
    IntegrationServiceId.HUBSPOT,
    IntegrationServiceId.REDTAIL,
    IntegrationServiceId.SALESFORCE,
    IntegrationServiceId.PRACTIFI,
    IntegrationServiceId.XLR8,
    IntegrationServiceId.ZOHO,
    IntegrationServiceId.DYNAMICS,
    IntegrationServiceId.SMARTOFFICE,
    IntegrationServiceId.ELEMENTS,
    IntegrationServiceId.BLACK_DIAMOND,
    IntegrationServiceId.ENGAGE,
    IntegrationServiceId.ADVISOR_ENGINE,
    IntegrationServiceId.INVESTCLOUD,
  ],
  [IntegrationCategory.DOCUMENT]: [
    IntegrationServiceId.BOX,
    IntegrationServiceId.EGNYTE,
    IntegrationServiceId.GOOGLE_DRIVE,
    IntegrationServiceId.ONEDRIVE,
    IntegrationServiceId.SHAREPOINT,
  ],
  [IntegrationCategory.CLIC]: [IntegrationServiceId.CLIC],
  [IntegrationCategory.ESTATE_PLANNING]: [IntegrationServiceId.WEALTH],
  [IntegrationCategory.CUSTODIAN]: [IntegrationServiceId.SCHWAB],
}

export const INTEGRATION_CATEGORY_DISPLAY_NAME_MAP: { [key in IntegrationCategory]: string } = {
  [IntegrationCategory.MEETING]: 'Meeting',
  [IntegrationCategory.CALLS]: 'Calls',
  [IntegrationCategory.MAIL]: 'Email',
  [IntegrationCategory.MARKETING_SOCIAL]: 'Marketing & Social',
  [IntegrationCategory.CALENDAR]: 'Calendar',
  [IntegrationCategory.CRM]: 'CRM',
  [IntegrationCategory.PLANNING]: 'Financial Planning',
  [IntegrationCategory.ONBOARDING]: 'Forms & Client Onboarding',
  [IntegrationCategory.PORTFOLIO]: 'Portfolio Management',
  [IntegrationCategory.TAX]: 'Tax Planning',
  [IntegrationCategory.DOCUMENT]: 'Document Management',
  [IntegrationCategory.CLIC]: 'CLIC®',
  [IntegrationCategory.ESTATE_PLANNING]: 'Estate Planning',
  [IntegrationCategory.CUSTODIAN]: 'Custodian',
}

export const INTEGRATION_DISPLAY_NAME_MAP: { [key in IntegrationServiceId]?: string } = {
  [IntegrationServiceId.E_MONEY]: 'eMoney',
  [IntegrationServiceId.RIGHT_CAPITAL]: 'RightCapital',
  [IntegrationServiceId.PRECISE_FP]: 'PreciseFP',
  [IntegrationServiceId.ORION]: 'Orion',
  [IntegrationServiceId.BLACK_DIAMOND_WP]: 'SS&C BlackDiamond Wealth Platform',
  [IntegrationServiceId.HOLISTIPLAN]: 'Holistiplan',
  [IntegrationServiceId.CLIC]: 'CLIC®',
  [IntegrationServiceId.ELEMENTS]: 'SS&C BlackDiamond Elements',
  [IntegrationServiceId.BLACK_DIAMOND]: 'SS&C BlackDiamond Edge',
  [IntegrationServiceId.ENGAGE]: 'SS&C BlackDiamond Engage',
  [IntegrationServiceId.INVESTCLOUD]: 'InvestCloud',
  [IntegrationServiceId.SMARTOFFICE]: 'SmartOffice',
  [IntegrationServiceId.ADVISOR_ENGINE]: 'AdvisorEngine',
  [IntegrationServiceId.GOOGLE_CALENDAR]: 'Google Calendar',
  [IntegrationServiceId.OUTLOOK_CALENDAR]: 'Outlook Calendar',
  [IntegrationServiceId.EXCHANGE_CALENDAR]: 'Exchange Calendar (On-Premise)',
  [IntegrationServiceId.MEET]: 'Google Meet',
  [IntegrationServiceId.TEAMS]: 'Microsoft Teams',
  [IntegrationServiceId.ZOOM]: 'Zoom',
  [IntegrationServiceId.OUTLOOK_MAIL]: 'Outlook Mail',
  [IntegrationServiceId.EXCHANGE_MAIL]: 'Exchange Mail (On-Premise)',
  [IntegrationServiceId.XLR8]: 'XLR8',
  [IntegrationServiceId.WEALTH]: 'Wealth.com',
  [IntegrationServiceId.WOVE]: 'Wove',
  [IntegrationServiceId.RINGCENTRAL]: 'RingCentral',
  [IntegrationServiceId.GOTO]: 'GoTo',
  [IntegrationServiceId.TEAMS_CALLS]: 'Microsoft Teams Calls',
  [IntegrationServiceId.DELEGATED_EMAIL]: 'Linked emails',
  [IntegrationServiceId.CONQUEST_PLANNING]: 'Conquest Planning',
  [IntegrationServiceId.SCHWAB]: 'Charles Schwab',
  [IntegrationServiceId.BOX]: 'Box',
  [IntegrationServiceId.ONEDRIVE]: 'OneDrive',
  [IntegrationServiceId.SHAREPOINT]: 'SharePoint',
  [IntegrationServiceId.EGNYTE]: 'Egnyte',
  [IntegrationServiceId.GOOGLE_DRIVE]: 'Google Drive',
  [IntegrationServiceId.LINKEDIN]: 'LinkedIn',
}

export const INTEGRATION_ADDITIONAL_QUOTA: { [key in IntegrationServiceId]?: string } = {
  [IntegrationServiceId.DELEGATED_EMAIL]: 'integration.delegatedEmailEnabled.value',
}

export const GOTO_HELP_ARTICLE_URL = 'https://help.zocks.io/en/articles/15700693-goto-connect-integration'

export const MICROSOFT_ADMIN_CONSENT_LINK = import.meta.env.REACT_APP_MICROSOFT_ADMIN_CONSENT_LINK!

export const ZOOM_MEETINGS_HELP_ARTICLE_URL = 'https://help.zocks.io/en/articles/9196497-how-to-integrate-zocks-with-meeting-providers'

export const ZOOM_PHONE_HELP_ARTICLE_URL = 'https://help.zocks.io/en/articles/16053579-zoom-phone-integration'

export const TEAMS_CALLS_HELP_ARTICLE_URL = 'https://help.zocks.io/en/articles/17582393-microsoft-teams-calls-integration'

export const INTEGRATIONS_WITH_SEGMENTED_CONTACT_CHANGE = [
  IntegrationServiceId.PRECISE_FP,
  IntegrationServiceId.E_MONEY,
  IntegrationServiceId.CLIC,
  IntegrationServiceId.RIGHT_CAPITAL,
  IntegrationServiceId.CONQUEST_PLANNING,
]

const CONTACT_LINKING_WITH_ADDITIONAL_OBJECTS_INTEGRATIONS: IntegrationServiceId[] = [IntegrationServiceId.WOVE]

export const isContactLinkingWithAdditionalObjects = (serviceId: IntegrationServiceId): boolean =>
  CONTACT_LINKING_WITH_ADDITIONAL_OBJECTS_INTEGRATIONS.includes(serviceId)

export const CROSS_CLIENT_QUERY_SUPPORTED_INTEGRATIONS = {
  [IntegrationCategory.CRM]: INTEGRATION_CATEGORY_MAP[IntegrationCategory.CRM],
  [IntegrationCategory.PLANNING]: INTEGRATION_CATEGORY_MAP[IntegrationCategory.PLANNING],
  [IntegrationCategory.TAX]: INTEGRATION_CATEGORY_MAP[IntegrationCategory.TAX],
  [IntegrationCategory.PORTFOLIO]: INTEGRATION_CATEGORY_MAP[IntegrationCategory.PORTFOLIO],
  [IntegrationCategory.ESTATE_PLANNING]: INTEGRATION_CATEGORY_MAP[IntegrationCategory.ESTATE_PLANNING],
}

export const getIntegrationServiceIdsForCategory = (category: IntegrationCategory): IntegrationServiceId[] => INTEGRATION_CATEGORY_MAP[category] || []

export const isDocumentConnectorServiceId = (serviceId: IntegrationServiceId): boolean =>
  getIntegrationServiceIdsForCategory(IntegrationCategory.DOCUMENT).includes(serviceId)

export const getIntegrationCategoryDisplayNameByServiceId = (serviceId: string): string => {
  const integrationServiceId = serviceId as IntegrationServiceId

  const category = Object.entries(INTEGRATION_CATEGORY_MAP).find(([, serviceIds]) => serviceIds.includes(integrationServiceId))?.[0] as
    | IntegrationCategory
    | undefined

  return category ? getIntegrationCategoryDisplayName(category) : 'Unknown'
}

export const getIntegrationDisplayName = (serviceId: IntegrationServiceId): string =>
  INTEGRATION_DISPLAY_NAME_MAP[serviceId] || serviceId.charAt(0).toUpperCase() + serviceId.slice(1)

export const sortIntegrationsByDisplayName = (integrations: ExternalUserServiceIntegration[]): ExternalUserServiceIntegration[] =>
  [...integrations].sort((a, b) => getIntegrationDisplayName(a.serviceId).localeCompare(getIntegrationDisplayName(b.serviceId)))

const INTEGRATION_SHORT_DISPLAY_NAME_MAP: Partial<Record<IntegrationServiceId, string>> = {
  [IntegrationServiceId.EXCHANGE_CALENDAR]: 'Exchange Calendar',
  [IntegrationServiceId.EXCHANGE_MAIL]: 'Exchange Mail',
}

export const getIntegrationShortDisplayName = (serviceId: IntegrationServiceId): string =>
  INTEGRATION_SHORT_DISPLAY_NAME_MAP[serviceId] ?? getIntegrationDisplayName(serviceId)

export const getIntegrationCategoryDisplayName = (category: IntegrationCategory): string =>
  INTEGRATION_CATEGORY_DISPLAY_NAME_MAP[category] || category.charAt(0).toUpperCase() + category.slice(1)

export const DEFAULT_ACCOUNT_INTEGRATION_CONFIG_MAPPINGS = {
  [AccountIntegrationConfigMappingType.CONTACT]: 'contact',
}

const DEFAULT_INTEGRATION_ENTITY_NAME = 'contact' as const

const INTEGRATION_ENTITY_NAME_OVERRIDES: Partial<Record<IntegrationServiceId, string>> = {
  [IntegrationServiceId.CONQUEST_PLANNING]: 'plan',
}

export const getRecordTypeName = (serviceId: IntegrationServiceId) => INTEGRATION_ENTITY_NAME_OVERRIDES[serviceId] ?? DEFAULT_INTEGRATION_ENTITY_NAME

const INTEGRATION_ENTITY_LABELS: Record<string, string> = {
  contact: 'Contact',
  plan: 'Plan',
}
export const getIntegrationEntityLabel = (serviceId: IntegrationServiceId) => INTEGRATION_ENTITY_LABELS[getRecordTypeName(serviceId)]

export const INTEGRATIONS_WITH_DYNAMIC_SCHEMA = [IntegrationServiceId.E_MONEY, IntegrationServiceId.PRECISE_FP, IntegrationServiceId.RIGHT_CAPITAL]

const DEFAULT_HIDDEN_INTEGRATIONS: IntegrationServiceId[] = [
  IntegrationServiceId.CLIC,
  IntegrationServiceId.INVESTCLOUD,
  IntegrationServiceId.WOVE,
  IntegrationServiceId.SCHWAB,
]

export const isIntegrationDefaultHidden = (serviceId: IntegrationServiceId): boolean => DEFAULT_HIDDEN_INTEGRATIONS.includes(serviceId)

const UN_CONNECTABLE_INTEGRATIONS: IntegrationServiceId[] = [IntegrationServiceId.TEAMS, IntegrationServiceId.MEET]

export const isIntegrationUnConnectable = (serviceId: IntegrationServiceId): boolean => UN_CONNECTABLE_INTEGRATIONS.includes(serviceId)

const INTEGRATION_ONLY_DISCONNECT: IntegrationServiceId[] = [IntegrationServiceId.CLIC, IntegrationServiceId.WOVE]

export const isIntegrationOnlyDisconnect = (serviceId: IntegrationServiceId): boolean => INTEGRATION_ONLY_DISCONNECT.includes(serviceId)

const INTEGRATION_ONLY_CONNECT: Set<IntegrationServiceId> = new Set([IntegrationServiceId.DELEGATED_EMAIL])

export const isIntegrationOnlyConnect = (serviceId: IntegrationServiceId): boolean => INTEGRATION_ONLY_CONNECT.has(serviceId)

export interface IntegrationActivityCodesConfig {
  entityType: 'activity_codes'
  defaultForNewActivity: number
  defaultForExportedTask: number
}

export const integrationActivityCodes: Partial<Record<IntegrationServiceId, IntegrationActivityCodesConfig>> = {
  [IntegrationServiceId.REDTAIL]: {
    entityType: 'activity_codes',
    defaultForNewActivity: 1,
    defaultForExportedTask: 4,
  },
}

export const getIntegrationActivityCodesConfig = (serviceId: IntegrationServiceId): IntegrationActivityCodesConfig | null =>
  integrationActivityCodes[serviceId] ?? null

export const defaultIntegrationMeetingExportObject: Partial<{ [key in IntegrationServiceId]: SelectOption }> = {
  [IntegrationServiceId.WEALTHBOX]: { id: 'notes', label: 'Note' },
  [IntegrationServiceId.SALESFORCE]: { id: 'Note', label: 'Note' },
  [IntegrationServiceId.ELEMENTS]: { id: 'ContentNote', label: 'Note' },
  [IntegrationServiceId.BLACK_DIAMOND]: { id: 'ContentNote', label: 'Note' },
  [IntegrationServiceId.PRACTIFI]: { id: 'ContentNote', label: 'Note' },
  [IntegrationServiceId.XLR8]: { id: 'XLR8CS__Critical_Note__c', label: 'Critical Note' },
  [IntegrationServiceId.HUBSPOT]: { id: 'note', label: 'Note' },
  [IntegrationServiceId.REDTAIL]: { id: 'notes', label: 'Note' },
  [IntegrationServiceId.ZOHO]: { id: 'Notes', label: 'Note' },
  [IntegrationServiceId.DYNAMICS]: { id: 'annotations', label: 'Note' },
  [IntegrationServiceId.ENGAGE]: { id: 'annotations', label: 'Note' },
  [IntegrationServiceId.SMARTOFFICE]: { id: 'SmartPad', label: 'Note' },
  [IntegrationServiceId.ADVISOR_ENGINE]: { id: 'workflows', label: 'Note' },
}

const FALLBACK_SESSION_OBJECT = 'note'

export const getDefaultSessionObject = (serviceId: IntegrationServiceId): string =>
  defaultIntegrationMeetingExportObject[serviceId]?.id ?? FALLBACK_SESSION_OBJECT

export interface TagsExportOptionConfig {
  entityType: Extract<IntegrationEntityType, 'note-tags' | 'contact-tags'>
  defaultTag: string
}

export interface VisibleToExportOptionConfig {
  entityType: Extract<IntegrationEntityType, 'user-groups'>
}

export interface ExportOptionsConfig {
  tags?: TagsExportOptionConfig
  visibleTo?: VisibleToExportOptionConfig
}

export interface ExportOptionsValues {
  tags?: string[]
  visibleTo?: string | null
}

const WEALTHBOX_VISIBLE_TO_EXPORT_OPTION: VisibleToExportOptionConfig = { entityType: 'user-groups' }

const integrationExportOptions: Partial<Record<IntegrationServiceId, Record<string, ExportOptionsConfig>>> = {
  [IntegrationServiceId.WEALTHBOX]: {
    notes: { tags: { entityType: 'note-tags', defaultTag: 'Zocks' }, visibleTo: WEALTHBOX_VISIBLE_TO_EXPORT_OPTION },
    events: { visibleTo: WEALTHBOX_VISIBLE_TO_EXPORT_OPTION },
  },
}

export const getIntegrationExportOptionsConfig = (serviceId: IntegrationServiceId, sessionObject: string): ExportOptionsConfig | null => {
  const sessionObjectConfigs = integrationExportOptions[serviceId]
  return sessionObjectConfigs && Object.hasOwn(sessionObjectConfigs, sessionObject) ? sessionObjectConfigs[sessionObject] : null
}

export const getDefaultExportOptionsValues = (config: ExportOptionsConfig | null): ExportOptionsValues => ({
  tags: config?.tags ? [config.tags.defaultTag] : [],
  visibleTo: null,
})

export const buildExportOptions = (config: ExportOptionsConfig | null, values?: ExportOptionsValues): ExportOptionsPayload | undefined => {
  if (!config || !values) {
    return undefined
  }
  const payload: ExportOptionsPayload = {
    ...(config.tags && values.tags?.length ? { tags: values.tags } : {}),
    ...(config.visibleTo && values.visibleTo ? { visibleTo: values.visibleTo } : {}),
  }
  return Object.keys(payload).length ? payload : undefined
}

export const defaultIntegrationContactObject: Partial<Record<IntegrationServiceId, string>> = {
  [IntegrationServiceId.WEALTHBOX]: 'Contact',
  [IntegrationServiceId.SALESFORCE]: 'Contact',
  [IntegrationServiceId.ELEMENTS]: 'Contact',
  [IntegrationServiceId.BLACK_DIAMOND]: 'Contact',
  [IntegrationServiceId.PRACTIFI]: 'Contact',
  [IntegrationServiceId.XLR8]: 'Contact',
  [IntegrationServiceId.HUBSPOT]: 'contact',
  [IntegrationServiceId.REDTAIL]: 'contact',
  [IntegrationServiceId.ZOHO]: 'contact',
  [IntegrationServiceId.DYNAMICS]: 'contacts',
  [IntegrationServiceId.ENGAGE]: 'contacts',
  [IntegrationServiceId.SMARTOFFICE]: 'contact',
  [IntegrationServiceId.ADVISOR_ENGINE]: 'contact',
  [IntegrationServiceId.WEALTH]: 'client',
}

export const ZOCKS_CONTACT_FIELDS = [
  { zocksKey: 'first_name', label: 'First Name' },
  { zocksKey: 'last_name', label: 'Last Name' },
  { zocksKey: 'name', label: 'Name' },
  { zocksKey: 'email', label: 'Email', required: true },
]

export const ZOCKS_CONTACT_DETAIL_FIELDS = [
  { zocksKey: 'birth_date', label: 'Date of Birth' },
  { zocksKey: 'gender', label: 'Gender' },
  { zocksKey: 'marital_status', label: 'Marital Status' },
  { zocksKey: 'employment_status', label: 'Employment Status' },
  { zocksKey: 'last_financial_plan_date', label: 'Last Financial Plan Date' },
  { zocksKey: 'last_ips_plan_date', label: 'Last IPS Plan Date' },
  { zocksKey: 'income', label: 'Total Gross Income' },
  { zocksKey: 'net_worth', label: 'Net Worth' },
  { zocksKey: 'total_assets', label: 'Total Assets' },
  { zocksKey: 'total_liabilities', label: 'Total Liabilities' },
]

export interface ZocksFieldMapping {
  zocksKey: string
  label: string
  required?: boolean
  allowStaticValue?: boolean
  /** Describe field types the row offers; unset offers every field. */
  valueTypes?: string[]
}

export const ZOCKS_EVENT_FIELDS: ZocksFieldMapping[] = [
  { zocksKey: 'summary', label: 'Title' },
  { zocksKey: 'description', label: 'Description' },
  { zocksKey: 'location', label: 'Location' },
  { zocksKey: 'start_at', label: 'Start Time', required: true },
  { zocksKey: 'end_at', label: 'End Time', required: true },
  { zocksKey: 'all_day', label: 'All Day' },
  { zocksKey: 'private', label: 'Private', valueTypes: ['boolean'] },
  { zocksKey: 'type', label: 'Event Type', allowStaticValue: true },
  { zocksKey: 'subtype', label: 'Event Subtype', allowStaticValue: true },
]

export const ZOCKS_EVENT_REQUIRED_FIELDS = ZOCKS_EVENT_FIELDS.filter((field) => field.required).map((field) => field.zocksKey)

export const CONTACT_DETAIL_FIELDS_SUPPORTED_INTEGRATIONS = [
  IntegrationServiceId.SALESFORCE,
  IntegrationServiceId.PRACTIFI,
  IntegrationServiceId.XLR8,
  IntegrationServiceId.ELEMENTS,
  IntegrationServiceId.BLACK_DIAMOND,
  IntegrationServiceId.ENGAGE,
  IntegrationServiceId.DYNAMICS,
]

export const CONTACT_SORT_SUPPORTED_INTEGRATIONS = [
  IntegrationServiceId.SALESFORCE,
  IntegrationServiceId.PRACTIFI,
  IntegrationServiceId.XLR8,
  IntegrationServiceId.ELEMENTS,
  IntegrationServiceId.BLACK_DIAMOND,
  IntegrationServiceId.DYNAMICS,
  IntegrationServiceId.ENGAGE,
]

export const SALESFORCE_BASED_INTEGRATIONS = [
  IntegrationServiceId.SALESFORCE,
  IntegrationServiceId.PRACTIFI,
  IntegrationServiceId.XLR8,
  IntegrationServiceId.ELEMENTS,
  IntegrationServiceId.BLACK_DIAMOND,
]

export interface HouseholdEventConfig {
  householdPath: string
  eventPath: string
}

export interface MappingConfig {
  path: string
  idKey: string
  labelKey: string
  household: HouseholdEventConfig | undefined
}

export const eventConfigMap: Partial<Record<IntegrationServiceId, MappingConfig>> = {
  [IntegrationServiceId.WEALTHBOX]: { path: 'events', idKey: 'id', labelKey: 'title', household: undefined },
  [IntegrationServiceId.REDTAIL]: { path: 'activities', idKey: 'id', labelKey: 'subject', household: undefined },
  [IntegrationServiceId.SALESFORCE]: {
    path: 'events',
    idKey: 'Id',
    labelKey: 'Subject',
    household: { householdPath: 'household', eventPath: 'events' },
  },
  [IntegrationServiceId.PRACTIFI]: {
    path: 'events',
    idKey: 'Id',
    labelKey: 'Subject',
    household: { householdPath: 'household', eventPath: 'events' },
  },
  [IntegrationServiceId.XLR8]: {
    path: 'events',
    idKey: 'Id',
    labelKey: 'Subject',
    household: { householdPath: 'household', eventPath: 'events' },
  },
  [IntegrationServiceId.ZOHO]: {
    path: 'events',
    idKey: 'id',
    labelKey: 'Event_Title',
    household: { householdPath: 'household', eventPath: 'events' },
  },
  [IntegrationServiceId.ELEMENTS]: {
    path: 'events',
    idKey: 'Id',
    labelKey: 'Subject',
    household: { householdPath: 'household', eventPath: 'events' },
  },
  [IntegrationServiceId.BLACK_DIAMOND]: {
    path: 'events',
    idKey: 'Id',
    labelKey: 'Subject',
    household: { householdPath: 'household', eventPath: 'events' },
  },
}

export const DEFAULT_CONTACT_SYNC_CHECKPOINT_OBJECT = 'contact_operation_hashed'

export const CONTACT_SYNC_CHECKPOINT_OBJECT: Partial<Record<IntegrationServiceId, string>> = {
  [IntegrationServiceId.WEALTHBOX]: 'contact',
}

export const CALENDAR_SYNC_CHECKPOINT_OBJECT: Partial<Record<IntegrationServiceId, string>> = {
  [IntegrationServiceId.WEALTHBOX]: 'calendar',
  [IntegrationServiceId.SALESFORCE]: 'event_operation_hashed',
}

export const ONBOARDING_CONTACT_SYNC_SUPPORTED_INTEGRATIONS: readonly IntegrationServiceId[] = [
  IntegrationServiceId.WEALTHBOX,
  IntegrationServiceId.HUBSPOT,
  IntegrationServiceId.REDTAIL,
  IntegrationServiceId.SALESFORCE,
  IntegrationServiceId.PRACTIFI,
  IntegrationServiceId.XLR8,
  IntegrationServiceId.ZOHO,
  IntegrationServiceId.DYNAMICS,
  IntegrationServiceId.SMARTOFFICE,
  IntegrationServiceId.BLACK_DIAMOND,
  IntegrationServiceId.ELEMENTS,
  IntegrationServiceId.ENGAGE,
  IntegrationServiceId.ADVISOR_ENGINE,
  IntegrationServiceId.E_MONEY,
  IntegrationServiceId.RIGHT_CAPITAL,
  IntegrationServiceId.SCHWAB,
]

export const ONBOARDING_CALENDAR_SYNC_SUPPORTED_INTEGRATIONS: readonly IntegrationServiceId[] = [
  IntegrationServiceId.WEALTHBOX,
  IntegrationServiceId.REDTAIL,
  IntegrationServiceId.SALESFORCE,
]

export enum OnboardingContactMappingCategory {
  DEFAULT_CONTACT_MAPPINGS = 'default_contact_mappings',
  NO_DEFAULT_FILTERS = 'no_default_filters',
  NO_MAPPING_OR_FILTER = 'no_mapping_or_filter',
}

const ONBOARDING_CONTACT_MAPPING_CATEGORY: Partial<Record<IntegrationServiceId, OnboardingContactMappingCategory>> = {
  [IntegrationServiceId.SALESFORCE]: OnboardingContactMappingCategory.DEFAULT_CONTACT_MAPPINGS,
  [IntegrationServiceId.PRACTIFI]: OnboardingContactMappingCategory.DEFAULT_CONTACT_MAPPINGS,
  [IntegrationServiceId.BLACK_DIAMOND]: OnboardingContactMappingCategory.DEFAULT_CONTACT_MAPPINGS,
  [IntegrationServiceId.ELEMENTS]: OnboardingContactMappingCategory.DEFAULT_CONTACT_MAPPINGS,
  [IntegrationServiceId.XLR8]: OnboardingContactMappingCategory.DEFAULT_CONTACT_MAPPINGS,
  [IntegrationServiceId.ZOHO]: OnboardingContactMappingCategory.DEFAULT_CONTACT_MAPPINGS,
  [IntegrationServiceId.DYNAMICS]: OnboardingContactMappingCategory.DEFAULT_CONTACT_MAPPINGS,
  [IntegrationServiceId.ENGAGE]: OnboardingContactMappingCategory.DEFAULT_CONTACT_MAPPINGS,
  [IntegrationServiceId.WEALTHBOX]: OnboardingContactMappingCategory.NO_DEFAULT_FILTERS,
  [IntegrationServiceId.REDTAIL]: OnboardingContactMappingCategory.NO_DEFAULT_FILTERS,
  [IntegrationServiceId.ADVISOR_ENGINE]: OnboardingContactMappingCategory.NO_MAPPING_OR_FILTER,
  [IntegrationServiceId.HUBSPOT]: OnboardingContactMappingCategory.NO_MAPPING_OR_FILTER,
  [IntegrationServiceId.SMARTOFFICE]: OnboardingContactMappingCategory.NO_MAPPING_OR_FILTER,
  [IntegrationServiceId.E_MONEY]: OnboardingContactMappingCategory.NO_MAPPING_OR_FILTER,
  [IntegrationServiceId.RIGHT_CAPITAL]: OnboardingContactMappingCategory.NO_MAPPING_OR_FILTER,
  [IntegrationServiceId.SCHWAB]: OnboardingContactMappingCategory.NO_MAPPING_OR_FILTER,
}

export const getOnboardingContactMappingCategory = (serviceId: IntegrationServiceId): OnboardingContactMappingCategory =>
  ONBOARDING_CONTACT_MAPPING_CATEGORY[serviceId] ?? OnboardingContactMappingCategory.NO_MAPPING_OR_FILTER

export const ONBOARDING_UNSUPPORTED_INTEGRATIONS = [IntegrationServiceId.DELEGATED_EMAIL]

export const DEFAULT_ADDITIONAL_OBJECTS_LIMIT = 10

export const BUSINESS_EVENT_EXPORT_ENABLED = SALESFORCE_BASED_INTEGRATIONS

export const CUSTOM_OBJECTS_ENABLED = SALESFORCE_BASED_INTEGRATIONS

export const CALENDAR_MAPPING_ENABLED = [IntegrationServiceId.SALESFORCE]

export const getBusinessEventLabel = (type: BusinessEventType): string => BUSINESS_EVENT_LABEL_MAP[type]

export const getBusinessEventDetails = (type: BusinessEventType): MentionData[] => BUSINESS_EVENT_DETAILS_MAPPING[type]

export const getEnabledBusinessEventExport = () => BUSINESS_EVENT_EXPORT_ENABLED

export const isBusinessEventExportEnabledForService = (serviceId: IntegrationServiceId) => BUSINESS_EVENT_EXPORT_ENABLED.includes(serviceId)

export const isCustomObjectsEnabledForService = (serviceId: IntegrationServiceId) => CUSTOM_OBJECTS_ENABLED.includes(serviceId)

export const isCalendarMappingEnabledForService = (serviceId: IntegrationServiceId) => CALENDAR_MAPPING_ENABLED.includes(serviceId)

/**
 * The contact object stored in the account config uses the preset's own naming (`contact`, `account`, `person_account`);
 * contact links address the real SObject, so the Salesforce-based integrations need it mapped back.
 */
export const getLinkableContactObjectName = (serviceId: IntegrationServiceId, objectName: string | null): string | null => {
  if (!SALESFORCE_BASED_INTEGRATIONS.includes(serviceId)) {
    return objectName
  }
  if (objectName === 'person_account' || objectName === 'account') {
    return 'Account'
  }
  if (objectName === 'contact') {
    return 'Contact'
  }
  return objectName
}

const CUSTOMIZABLE_ADMIN_MAPPING_SETTINGS_INTEGRATIONS = new Set<IntegrationServiceId>([
  IntegrationServiceId.SALESFORCE,
  IntegrationServiceId.PRACTIFI,
  IntegrationServiceId.XLR8,
  IntegrationServiceId.BLACK_DIAMOND,
  IntegrationServiceId.ELEMENTS,
  IntegrationServiceId.DYNAMICS,
  IntegrationServiceId.ENGAGE,
])

const PREDEFINED_ADMIN_MAPPING_SETTINGS_INTEGRATIONS = new Set<IntegrationServiceId>([
  IntegrationServiceId.REDTAIL,
  IntegrationServiceId.WEALTHBOX,
  IntegrationServiceId.ZOHO,
])

export const integrationHasCustomizableAdminMappingSettings = (serviceId: IntegrationServiceId): boolean =>
  CUSTOMIZABLE_ADMIN_MAPPING_SETTINGS_INTEGRATIONS.has(serviceId)

export const integrationHasPredefinedAdminMappingSettings = (serviceId: IntegrationServiceId): boolean =>
  PREDEFINED_ADMIN_MAPPING_SETTINGS_INTEGRATIONS.has(serviceId)

export const integrationHasAdminMappingSettings = (serviceId: IntegrationServiceId): boolean =>
  integrationHasCustomizableAdminMappingSettings(serviceId) || integrationHasPredefinedAdminMappingSettings(serviceId)

interface CanCreateContactForUserParams {
  serviceId: IntegrationServiceId
  userType?: string
}

const REQUIRES_CONNECTED_INTEGRATION_FOR_CONTACT_CREATE = new Set<IntegrationServiceId>([IntegrationServiceId.CONQUEST_PLANNING])

export const canCreateContactForUser = ({ serviceId, userType }: CanCreateContactForUserParams): boolean =>
  !REQUIRES_CONNECTED_INTEGRATION_FOR_CONTACT_CREATE.has(serviceId) || userType !== 'anonymous'

export type ContactActionsAccountConfig = Pick<CRMAccountIntegrationConfig, 'enableContactCreate' | 'enableContactUpdate'>

export const isContactCreateEnabledInConfig = (config: Partial<ContactActionsAccountConfig> | null | undefined): boolean => config?.enableContactCreate ?? true

export const isContactUpdateEnabledInConfig = (config: Partial<ContactActionsAccountConfig> | null | undefined): boolean => config?.enableContactUpdate ?? true

export const isFullMeetingExportEnabledInConfig = (config: CRMAccountIntegrationConfig | null): boolean => !config || config.enableFullMeetingExport

const OWNER_SELECTION_REQUIRED_SERVICES = [IntegrationServiceId.E_MONEY]

export const integrationRequiresOwnerSelection = (serviceId: IntegrationServiceId): boolean => {
  return OWNER_SELECTION_REQUIRED_SERVICES.includes(serviceId)
}

export type ConquestPlanningEnvironment = 'CDN' | 'UK' | 'USA'
